import { ComponentProps, useCallback, useState } from 'react';
import { PopoverClose } from '@radix-ui/react-popover';
import { DateRange, Matcher } from 'react-day-picker';
import { useTranslation } from '@sib/i18n';
import {
  Button,
  ButtonProps,
  Calendar,
  cn,
  containerVariants,
  formatRangeLabel,
  Loader,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@sib/ui';
import { Calendar as CalendarIcon } from '@sib/ui/icons';
import { X } from '@sib/ui/icons';
import { useDatetimePicker } from '../../../hooks';

export type DateRangeType = DateRange;

export interface DateRangePickerProps
  extends Omit<
    ComponentProps<typeof Calendar>,
    'selected' | 'onSelect' | 'mode' | 'disabled'
  > {
  selected: DateRange | null;
  onSelect: (range: DateRange | null) => void;
  confirmLabel?: string;
  clearLabel?: string;
  buttonProps?: ButtonProps;
  dateFormat?: string;
  placeholder?: string;
  defaultDate?: Date | null;
  disabled?: Matcher | Matcher[] | boolean;
  loading?: boolean;
  PopoverContentProps?: ComponentProps<typeof PopoverContent>;
  closeOnScroll?: boolean;
}

export const DEFAULT_DATE_FORMAT = 'MM/dd/yyyy';
const DEFAULT_SINGLE_DATE_FORMAT = 'PPP';

export const DateRangePicker = ({
  selected,
  onSelect,
  confirmLabel,
  clearLabel,
  buttonProps,
  dateFormat = DEFAULT_DATE_FORMAT,
  placeholder,
  defaultDate,
  disabled,
  loading,
  PopoverContentProps,
  closeOnScroll,
  defaultMonth,
  ...props
}: DateRangePickerProps) => {
  const { t } = useTranslation();
  const { isOpen, setIsOpen, resolveDisabledProps } = useDatetimePicker({
    closeOnScroll,
  });
  const [pendingRange, setPendingRange] = useState<DateRange | undefined>(
    undefined
  );

  const handleOpenChange = useCallback(
    (open: boolean) => {
      setPendingRange(open ? (selected ?? undefined) : undefined);
      setIsOpen(open);
    },
    [selected, setIsOpen]
  );

  const handleConfirm = useCallback(() => {
    onSelect(pendingRange ?? null);
    setIsOpen(false);
  }, [onSelect, pendingRange, setIsOpen]);

  const handleClear = useCallback(() => {
    setPendingRange(undefined);
    onSelect(null);
    setIsOpen(false);
  }, [onSelect, setIsOpen]);

  const { isButtonDisabled, calendarDisabled } = resolveDisabledProps(disabled);

  const mappedDefaultDate = defaultDate || undefined;
  const label = formatRangeLabel(
    selected ?? undefined,
    dateFormat,
    DEFAULT_SINGLE_DATE_FORMAT
  );
  const isConfirmDisabled = !pendingRange?.from;

  const finalConfirmLabel = confirmLabel ?? t('apply');
  const finalClearLabel = clearLabel ?? t('clear');

  return (
    <Popover modal open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild className="w-full">
        <Button
          {...buttonProps}
          variant="ghost"
          disabled={buttonProps?.disabled || isButtonDisabled || loading}
          className={cn(
            containerVariants({ variant: 'base' }),
            'items-center justify-start',
            !selected && 'text-muted-foreground',
            buttonProps?.className
          )}
        >
          {loading ? (
            <Loader className="size-5 shrink-0" />
          ) : (
            <CalendarIcon className="size-5 shrink-0" />
          )}
          {label ? (
            <span className="truncate">{label}</span>
          ) : (
            <span className="leading-6">{placeholder}</span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        {...PopoverContentProps}
        className={cn(
          'w-auto border-none p-0 shadow-[0px_24px_36px_0px_rgba(69,73,77,0.24)]',
          PopoverContentProps?.className
        )}
        onClick={e => e.stopPropagation()}
      >
        <PopoverClose className="text-primary absolute top-1 right-1 z-200 cursor-pointer p-2 hover:bg-[#F6F8FA] hover:brightness-100">
          <X className="size-3" />
        </PopoverClose>
        <Calendar
          {...props}
          mode="range"
          selected={pendingRange}
          defaultMonth={defaultMonth ?? pendingRange?.from ?? mappedDefaultDate}
          disabled={calendarDisabled}
          onSelect={setPendingRange}
        />
        <div className="flex justify-center gap-2 pb-2">
          <Button
            variant="ghost"
            size="sm"
            disabled={!pendingRange?.from && !selected}
            onClick={handleClear}
          >
            {finalClearLabel}
          </Button>
          <Button
            variant="default"
            size="sm"
            disabled={isConfirmDisabled}
            onClick={handleConfirm}
          >
            {finalConfirmLabel}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};
